import React, { FC, useEffect, useState } from 'react';

// Services
import {
  createContactCase,
  getContactFormPageData,
} from '../../services/formService';

// Components
import { ContactForm } from '../../components/forms';
import { CircleLoader } from '../../components/common';

// Context
import { useGlobalState } from '../../context/global-context/hooks';

// Models
import { ContactFormValue } from '../../models/Forms/ContactFormValue';
import { ContactFormPageContent } from '../../models/Responses/ContactFormPageContent';
import { PageView } from '../../models/PageView';

// ii18n
import { useTranslation } from '../../translations/useTranslation';

// Utils
import { scrollToTop } from '../../utilities/utils';

// Hoooks
import { usePrevious } from '../../hooks/usePrevious';

const initialValues: ContactFormValue = {
  subject: { Title: '', EmailRecipient: undefined },
  name: '',
  email: '',
  emailRepeated: '',
  phone: '',
  message: '',
  socialSecurityNumber: '',
  attachment: undefined,
};

const ContactFormPage: FC = () => {
  const [view, setView] = useState<PageView>('loading');
  const { prev: prevView } = usePrevious<PageView>(view);
  const [pageContent, setPageContent] = useState<ContactFormPageContent>();
  const [formState, setFormState] = useState<ContactFormValue>(initialValues);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const { state } = useGlobalState();
  const { t, keys } = useTranslation();

  useEffect(() => {
    getContactFormPageData(state.pageId).then((response) => {
      setView('form');
      setPageContent(response.data);
    });
  }, []);

  useEffect(() => {
    // Scroll to top when view changes
    if (!prevView || prevView === 'loading') return;
    scrollToTop();
  }, [view]);

  const handleSubmit = async (values: ContactFormValue) => {
    setFormState(values);
    try {
      const response = await createContactCase({
        subject: values.subject.Title,
        recipient: values.subject.EmailRecipient,
        name: values.name,
        email: values.email,
        message: values.message,
        socialSecurityNumber: values.socialSecurityNumber,
        phone: values.phone,
        umbracoPageId: state.pageId,
        attachment: values.attachment,
      });
      if (response.data.ThankYouPageUrl) {
        setIsRedirecting(true);
        location.href = response.data.ThankYouPageUrl;
      } else {
        resetForm();
        resetView();
      }
    } catch (err) {
      // TODO: Custom error messages depending on status code?
      setView('error');
    }
  };

  const resetView = () => {
    setView('form');
  };

  const resetForm = () => {
    setFormState(initialValues);
  };

  return (
    <div className="form-app__contact-form">
      {view === 'form' && (
        <ContactForm
          formValues={formState}
          subjects={pageContent?.Subjects || []}
          isRedirecting={isRedirecting}
          onSubmit={handleSubmit}
        />
      )}
      {view === 'loading' && <CircleLoader size="sm" />}
      {view === 'error' && (
        <div>
          <h2>{t(keys.uiErrorTitle)}...</h2>
          <p>{t(keys.resumedMemberShipFormErrorMessage)}</p>
          <button
            className="button form-app__button form-app__button--auto-width"
            onClick={resetView}
          >
            {t(keys.uiTryAgain)}
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactFormPage;
